import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { parseISO } from 'date-fns';
import { CircleDollarSign, UserRoundCheck, UserRoundPlus } from 'lucide-react';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { ConversionStats } from '@/components/ui/conversion-stats';
import { MetricGroup, MetricGroupItem } from '@/components/ui/metric-group';
import { Skeleton } from '@/components/ui/skeleton';
import { Typography } from '@/components/ui/typography';
import { useTableContext } from '@/contexts/table-context';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';
import { cn } from '@/utils/classnames';

import { useHiresToDate } from '../hires-to-date/context';
import { HiresAfterRangeChip } from '../hires-to-date/hires-after-range-chip';
import {
  RECOUNT_TINT_CLASS,
  useRecountTint,
} from '../hires-to-date/recount-tint';
import { HiresToDateSwitch } from '../hires-to-date/hires-to-date-switch';
import { useCampaignsMetricsQueryOptions } from './use-campaigns-metrics-query';

export function CampaignsMetricsContent() {
  const intl = useIntl();
  const { formatCurrency } = useCurrencySymbol();
  const { dateRange } = useTableContext();
  const { isEligible, isOn, setOn, today } = useHiresToDate();
  const { data } = useSuspenseQuery(useCampaignsMetricsQueryOptions());
  // Applicants, spend and CPA never change with the switch, so only hires,
  // conversion and CPH wait on the to-date request.
  const toDate = useQuery({
    ...useCampaignsMetricsQueryOptions({ hiresToDate: true }),
    enabled: isOn,
  });
  const counted = isOn ? toDate.data : data;
  const isTinting = useRecountTint(!counted && !toDate.isError);
  const recountedClass = cn(
    '-mx-1 w-fit px-1',
    isTinting && RECOUNT_TINT_CLASS,
  );
  const [hasToggled, setHasToggled] = useState(false);

  const toggleHiresToDate = (next: boolean) => {
    setOn(next);
    setHasToggled(true);
  };

  const longDate = (date: Date) =>
    intl.formatDate(date, { month: 'short', day: '2-digit', year: 'numeric' });

  // On a range the switch can count, the card grows to fit the caption with its
  // cut-off suffix, so the caption sits a gap away from the card title. The
  // suffix keeps its room while the switch is off, so flipping it moves nothing.
  const spendCostHeaderLabel = (
    <span className={cn(isEligible && 'pl-2')}>
      {intl.formatMessage(
        {
          defaultMessage:
            'Based on {applicantCount} applicants across {channelCount} campaigns',
          description:
            'Contextual caption on the spend and cost metrics card, showing how many applicants and channels the totals below are drawn from',
        },
        {
          applicantCount: data.caption.applicant_count,
          channelCount: data.caption.channel_count,
        },
      )}
      {isEligible && (
        <span className={cn(!isOn && 'invisible')} aria-hidden={!isOn}>
          {' '}
          {intl.formatMessage(
            {
              defaultMessage: '· hires to {today}',
              description:
                'Suffix of the spend and cost metrics caption with Hires to date on: the day hires are counted to, eg "· hires to Oct 22"',
            },
            {
              today: intl.formatDate(today, { month: 'short', day: '2-digit' }),
            },
          )}
        </span>
      )}
    </span>
  );

  const sourcedRange =
    isEligible && dateRange.startDate && dateRange.endDate
      ? intl.formatMessage(
          {
            defaultMessage:
              'Applicants sourced {startDate} – {endDate}. Hires counted to {cutoff}.',
            description:
              'Sourced this period tooltip on All Campaigns for a past date range: the range applicants come from and the day hires count to',
          },
          {
            startDate: longDate(parseISO(dateRange.startDate)),
            endDate: longDate(parseISO(dateRange.endDate)),
            cutoff: longDate(isOn ? today : parseISO(dateRange.endDate)),
          },
        )
      : null;

  const hiresAnnouncement =
    hasToggled && counted
      ? intl.formatMessage(
          {
            defaultMessage: 'Hires updated to {count}.',
            description:
              'Screen reader announcement after the Hires to date switch on All Campaigns recounts the Hires card',
          },
          { count: intl.formatNumber(counted.hires) },
        )
      : '';
  // A failed to-date request shows "-", as a missing value does, rather than a
  // skeleton that never resolves; the in-range query errors to the boundary.
  // The Spend card widens to keep its cut-off caption on one line, so the
  // Sourced columns give up the room.
  const sourcedItemWidth = isEligible ? 'min-w-[120px]' : undefined;
  const recalculating = toDate.isError ? (
    <Typography variant="large2">-</Typography>
  ) : (
    <Skeleton className="mt-2 h-4 w-12" />
  );

  return (
    <div className="shrink-0 overflow-x-auto">
      <div aria-live="polite" className="sr-only">
        {hiresAnnouncement}
      </div>
      <div className="flex min-w-max flex-row items-stretch gap-4 pb-2">
        {/* Sourced this period */}
        <MetricGroup
          label={intl.formatMessage({
            defaultMessage: 'Sourced this period',
            description:
              'Heading for a metric group showing applicants and hires that came from sourcing campaigns in the current period',
          })}
          icon={<UserRoundPlus className="text-fx-neutral-400 size-4" />}
          className="min-w-0 flex-1"
          tooltipTitle={intl.formatMessage({
            defaultMessage: 'Sourced this period',
            description:
              'Introduction to an explanation of which applicants and hires are included in the sourced period count',
          })}
          tooltipBody={
            <>
              {intl.formatMessage({
                defaultMessage:
                  'Applicants and hires limited to people you sourced in this period. This is the population your campaign activity drove.',
                description:
                  'Explanation that this group counts only applicants and hires from people sourced through campaigns in this period',
              })}
              {sourcedRange && (
                <span className="mt-1 block">{sourcedRange}</span>
              )}
            </>
          }
          headerAction={
            <HiresToDateSwitch
              onToggle={toggleHiresToDate}
              hiresAfterRangeEnd={data.hires_after_range_end ?? 0}
            />
          }
          isCard
        >
          <MetricGroupItem
            className={sourcedItemWidth}
            label={intl.formatMessage({
              defaultMessage: 'Applicants',
              description:
                'Number of people who applied through sourcing campaigns in the period',
            })}
          >
            <Typography variant="large2">{data.applicants || '-'}</Typography>
          </MetricGroupItem>

          <MetricGroupItem
            className={sourcedItemWidth}
            label={
              isOn
                ? intl.formatMessage({
                    defaultMessage: 'Hires to date',
                    description:
                      'All Campaigns Hires card label with Hires to date on: the range applicants hired up to today',
                  })
                : intl.formatMessage({
                    defaultMessage: 'Hires',
                    description:
                      'Number of people hired who were sourced through campaigns in the period',
                  })
            }
          >
            {counted ? (
              <div className="flex items-center gap-2">
                <div className={recountedClass}>
                  <Typography variant="large2">
                    {counted.hires || '-'}
                  </Typography>
                </div>
                <HiresAfterRangeChip
                  count={data.hires_after_range_end ?? 0}
                  onTurnOn={() => toggleHiresToDate(true)}
                />
              </div>
            ) : (
              recalculating
            )}
          </MetricGroupItem>

          <MetricGroupItem
            className={sourcedItemWidth}
            label={
              isOn
                ? intl.formatMessage({
                    defaultMessage: 'Conv. to date',
                    description:
                      'All Campaigns conversion label with Hires to date on: conversion from the range applicants to hires up to today',
                  })
                : intl.formatMessage({
                    defaultMessage: 'Conv. To Hire',
                    description:
                      'Conversion rate from sourced applicant to hire, expressed as a percentage',
                  })
            }
          >
            {counted ? (
              <div className={recountedClass}>
                <ConversionStats
                  stats={{ actual: counted.conversion_to_hire, expected: null }}
                  typographyVariant="large2"
                  showOnlyActual
                />
              </div>
            ) : (
              recalculating
            )}
          </MetricGroupItem>
        </MetricGroup>

        {/* Spend & cost metrics */}
        <MetricGroup
          label={intl.formatMessage({
            defaultMessage: 'Spend & cost metrics',
            description:
              'Heading for a metric group showing spend, cost per applicant, and cost per hire for sourcing campaigns',
          })}
          icon={<CircleDollarSign className="text-fx-neutral-400 size-4" />}
          // The caption reserves the cut-off date on a range the switch can
          // count, and the card grows past its half to keep it on one line.
          className={cn('flex-1', isEligible ? 'min-w-max' : 'min-w-0')}
          headerLabel={spendCostHeaderLabel}
          tooltipTitle={intl.formatMessage({
            defaultMessage: 'Spend & cost metrics',
            description:
              'Introduction to an explanation of what the spend and cost metrics card measures',
          })}
          tooltipBody={intl.formatMessage(
            {
              defaultMessage:
                'These are spend and cost metrics for the {channelCount} campaigns posted in Fountain, which together acquired ~{applicantCount} applicants. Cost divides spend by applicants who came through those integrated campaigns.',
              description:
                'Explanation of how spend and cost metrics are calculated: {channelCount} is the number of campaigns and {applicantCount} is the approximate number of applicants they acquired',
            },
            {
              channelCount: data.caption.channel_count,
              applicantCount: data.caption.applicant_count,
            },
          )}
          isCard
        >
          <MetricGroupItem
            label={intl.formatMessage({
              defaultMessage: 'Spend',
              description: 'Total amount of money spent on sourcing campaigns',
            })}
          >
            <Typography variant="large2">
              {formatCurrency(data.total_spend)}
            </Typography>
          </MetricGroupItem>

          <MetricGroupItem
            label={intl.formatMessage({
              defaultMessage: 'Avg. CPA',
              description:
                'Average cost per applicant across sourcing campaigns (CPA = cost per applicant)',
            })}
          >
            <Typography variant="large2">
              {data.avg_cpa != null ? formatCurrency(data.avg_cpa) : '-'}
            </Typography>
          </MetricGroupItem>

          <MetricGroupItem
            label={
              isOn
                ? intl.formatMessage({
                    defaultMessage: 'Avg. CPH to date',
                    description:
                      'All Campaigns cost per hire label with Hires to date on: spend divided by hires up to today',
                  })
                : intl.formatMessage({
                    defaultMessage: 'Avg. CPH',
                    description:
                      'Average cost per hire across sourcing campaigns (CPH = cost per hire)',
                  })
            }
          >
            {counted ? (
              <div className={recountedClass}>
                <Typography variant="large2">
                  {counted.avg_cph != null
                    ? formatCurrency(counted.avg_cph)
                    : '-'}
                </Typography>
              </div>
            ) : (
              recalculating
            )}
          </MetricGroupItem>
        </MetricGroup>
      </div>
    </div>
  );
}

export function CampaignsMetricsSkeleton() {
  const intl = useIntl();

  return (
    <div className="shrink-0 overflow-x-auto">
      <div className="flex min-w-max flex-row items-stretch gap-4 pb-2">
        <MetricGroup
          label={intl.formatMessage({
            defaultMessage: 'Sourced this period',
            description:
              'Heading for a metric group showing applicants and hires that came from sourcing campaigns in the current period',
          })}
          icon={<UserRoundPlus className="text-fx-neutral-400 size-4" />}
          className="min-w-0 flex-1"
          isCard
        >
          <MetricGroupItem
            label={intl.formatMessage({
              defaultMessage: 'Applicants',
              description:
                'Number of people who applied through sourcing campaigns in the period',
            })}
          >
            <Skeleton className="mt-2 h-4 w-12" />
          </MetricGroupItem>
          <MetricGroupItem
            label={intl.formatMessage({
              defaultMessage: 'Hires',
              description:
                'Number of people hired who were sourced through campaigns in the period',
            })}
          >
            <Skeleton className="mt-2 h-4 w-12" />
          </MetricGroupItem>
          <MetricGroupItem
            label={intl.formatMessage({
              defaultMessage: 'Conv. To Hire',
              description:
                'Conversion rate from sourced applicant to hire, expressed as a percentage',
            })}
          >
            <Skeleton className="mt-2 h-4 w-12" />
          </MetricGroupItem>
        </MetricGroup>

        <MetricGroup
          label={intl.formatMessage({
            defaultMessage: 'Spend & cost metrics',
            description:
              'Heading for a metric group showing spend, cost per applicant, and cost per hire for sourcing campaigns',
          })}
          icon={<CircleDollarSign className="text-fx-neutral-400 size-4" />}
          className="min-w-0 flex-1"
          isCard
        >
          <MetricGroupItem
            label={intl.formatMessage({
              defaultMessage: 'Spend',
              description: 'Total amount of money spent on sourcing campaigns',
            })}
          >
            <Skeleton className="mt-2 h-4 w-12" />
          </MetricGroupItem>
          <MetricGroupItem
            label={intl.formatMessage({
              defaultMessage: 'Avg. CPA',
              description:
                'Average cost per applicant across sourcing campaigns (CPA = cost per applicant)',
            })}
          >
            <Skeleton className="mt-2 h-4 w-12" />
          </MetricGroupItem>
          <MetricGroupItem
            label={intl.formatMessage({
              defaultMessage: 'Avg. CPH',
              description:
                'Average cost per hire across sourcing campaigns (CPH = cost per hire)',
            })}
          >
            <Skeleton className="mt-2 h-4 w-12" />
          </MetricGroupItem>
        </MetricGroup>
      </div>
    </div>
  );
}
